import type { ProposalChangeDescriptor } from "work/proposals/negotiation/proposal-change-description";

/**
 * The topical shape of a change list.
 *
 * A submission's changes arrive in the order they happened, which is the order the *author* made them in
 * and no order at all to the person reading them: two team leaders separated by a date field, a document
 * between a discount and a name. A leader checking what they are about to send reads by subject — "who is
 * on my team", "what did the fee schedule do" — so the list is grouped the way the proposal editor's own
 * tabs are, with client and vendor team changes kept under separate headings so identical labels such
 * as "Team Leader" retain their party context.
 *
 * Membership and ordering come from one table: a section owns a list of path patterns, a change belongs to
 * the first section whose pattern it matches, and it sorts by which pattern that was. So the order within
 * a section is the proposal's own field order rather than the edit order, and adding a field means adding
 * one pattern in one place.
 */

export type ProposalChangeSection = {
  title: string;
  changes: ProposalChangeDescriptor[];
};

/** Anything on a path this module has never heard of — a newer API's field — still gets shown. */
export const proposalOtherChangesSectionTitle = "Other Changes";

type ProposalChangeSectionDefinition = {
  title: string;
  /** Ordered: membership is the first match, and the match index is the field's rank in the section. */
  fields: readonly RegExp[];
};

/**
 * Sections in the order they are shown, mirroring the proposal editor's tabs (Details, Team, Fee Schedule,
 * Conflicts, Policies, Discount) so a leader meets the changes in the same sequence as the fields they
 * edited.
 */
const proposalChangeSectionDefinitions: readonly ProposalChangeSectionDefinition[] = [
  {
    title: "Details",
    fields: [
      /^\/name(?:\/|$)/,
      /^\/description(?:\/|$)/,
      /^\/negotiable(?:\/|$)/,
      /^\/responseDueBy(?:\/|$)/,
      /^\/startDate(?:\/|$)/,
      /^\/endDate(?:\/|$)/,
    ],
  },
  {
    title: "Team",
    fields: [
      /^\/teamRestricted(?:\/|$)/,
      /^\/reviewers(?:\/|$)/,
    ],
  },
  {
    title: "Client Team",
    fields: [
      /^\/clientTeam\/leader(?:\/|$)/,
      /^\/clientTeam\/members(?:\/|$)/,
      /^\/clientTeam(?:\/|$)/,
      /^\/clientReviewers(?:\/|$)/,
    ],
  },
  {
    title: "Vendor Team",
    fields: [
      /^\/vendorTeam\/leader(?:\/|$)/,
      /^\/vendorTeam\/members(?:\/|$)/,
      /^\/vendorTeam(?:\/|$)/,
      /^\/vendorReviewers(?:\/|$)/,
    ],
  },
  { title: "Fee Schedule", fields: [/^\/feeSchedule(?:\/|$)/] },
  { title: "Conflicts", fields: [/^\/conflictsCheckWaived(?:\/|$)/, /^\/conflicts(?:\/|$)/] },
  {
    title: "Policies",
    fields: [/^\/clientPolicies(?:\/|$)/, /^\/vendorPolicies(?:\/|$)/],
  },
  { title: "Discount", fields: [/^\/discount(?:\/|$)/] },
];

/** The heading a change is filed under — "Client Team" for `/clientTeam/leader`, "Details" for `/name`. */
export function proposalChangeSectionTitle(path?: string | null): string {
  return placementFor(path).title;
}

/**
 * Groups changes topically, dropping sections nothing landed in.
 *
 * Within a section, changes sort by their field's position in the proposal, then by the name of the thing
 * they happened to (two team members read alphabetically rather than in the order they were picked), and
 * finally by their position in the input so the grouping is stable for two changes that are alike in every
 * other respect.
 */
export function groupProposalChangesBySection(
  changes: readonly ProposalChangeDescriptor[],
): ProposalChangeSection[] {
  const ordered = changes
    .map((change, index) => ({ change, index, placement: placementFor(change.path) }))
    .sort(
      (left, right) =>
        left.placement.sectionRank - right.placement.sectionRank ||
        left.placement.fieldRank - right.placement.fieldRank ||
        subjectText(left.change).localeCompare(subjectText(right.change)) ||
        left.index - right.index,
    );

  const sections: ProposalChangeSection[] = [];
  for (const { change, placement } of ordered) {
    const current = sections.at(-1);
    if (current?.title === placement.title) current.changes.push(change);
    else sections.push({ title: placement.title, changes: [change] });
  }
  return sections;
}

type ProposalChangeSectionPlacement = { title: string; sectionRank: number; fieldRank: number };

function placementFor(path?: string | null): ProposalChangeSectionPlacement {
  if (path) {
    for (const [sectionRank, section] of proposalChangeSectionDefinitions.entries()) {
      const fieldRank = section.fields.findIndex((pattern) => pattern.test(path));
      if (fieldRank >= 0) return { title: section.title, sectionRank, fieldRank };
    }
  }
  return {
    title: proposalOtherChangesSectionTitle,
    sectionRank: proposalChangeSectionDefinitions.length,
    fieldRank: 0,
  };
}

/**
 * What a change is *about*, for ordering alike rows: the item's own name when it has one, and otherwise
 * the value it carries — a team member has no item label of its own (a member is two ids), so the resolved
 * person's name is the only thing that names the row.
 */
function subjectText(change: ProposalChangeDescriptor): string {
  return change.itemLabel ?? change.nextText ?? change.previousText ?? "";
}
